import React, { useEffect } from 'react'
import styled from 'styled-components'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { CardNumberElement } from '@stripe/react-stripe-js'

// Components
import { useStripe } from 'containers/StripeProvider'
import { Caption, Button, Form } from '@sketch/components'
import Modal from 'components/Modal'
import { useToast } from '@sketch/toasts'
import { ModalInjectedProps } from 'containers/ModalManager'
import Banner from 'components/Banner'

// Local components
import CreditCardForm from 'modules/workspace/components/CreditCardForm'
import CreditCards from 'modules/workspace/components/CreditCardLogos'

// Validations
import { createFormikValidator } from 'modules/workspace/components/StripeField'

// Types
import { useUpdatePaymentDetailsMutation } from '@sketch/gql-types'

// Utils
import { confirmPendingSetupIntent } from '../../utils'

// Styles
import Fieldset from 'modules/workspace/components/Fieldset'

const Strong = styled.strong`
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

// Formik Validation
const VALIDATION_SCHEMA = yup.object().shape({
  cardName: yup.string().trim().required('Name on Card is required'),
})

const INITIAL_VALUES = {
  cardName: '',

  /**
   * These fields are related with the credit-card
   * data, in order to get formik like validation
   * we need to include them in the "initial values"
   *
   * these values are only placeholders, in the end,
   * none of their values will be used
   */
  cardNumber: undefined,
  cardCvc: undefined,
  cardExpirationDate: undefined,
}

type FormValues = typeof INITIAL_VALUES

interface EditPaymentMethodModalProps extends ModalInjectedProps {
  customerId: string
  onPaymentUpdated?: () => Promise<void>
  isWorkspaceSubscriptionActive: boolean
}

const stripeValidation = createFormikValidator(
  ['cardCvc', 'cardExpirationDate', 'cardNumber'],
  {
    cardNumber: 'Card Number is required',
    cardCvc: 'CVC is required',
    cardExpirationDate: 'Expiry Date is required',
  }
)

/**
 * EditPaymentMethodModal
 *
 * Renders the modal that allows the user to edit the payment method
 * in the Workspace Settings > Billing page inside the Payment Details panel
 */
const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({
  customerId,
  onPaymentUpdated,
  isWorkspaceSubscriptionActive,
  hideModal,
}) => {
  const { showToast } = useToast()
  const { load, status, stripe, elements } = useStripe()

  /* Load stripe */
  useEffect(() => {
    if (status !== 'success') {
      load()
    }
  }, [load, status])

  // MUTATIONS
  const [updatePaymentDetails, { loading }] = useUpdatePaymentDetailsMutation({
    onError: 'show-toast',
  })

  // HANDLERS
  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    if (!stripe || !elements) {
      showToast('Stripe module is not available', 'negative')
      return
    } else {
      const cardElement = elements.getElement(CardNumberElement)
      const { error, token } = await stripe.createToken(cardElement!, {
        name: values.cardName,
      })

      if (error || !token) {
        showToast(
          error?.message || "It wasn't possible to use your credit-card",
          'negative'
        )
      } else {
        let subscription = null

        try {
          const { data } = await updatePaymentDetails({
            variables: {
              input: {
                customerId,
                paymentSource: token.id,
              },
            },
          })

          subscription = data?.updatePaymentDetails.customer.subscription
        } catch (e) {
          // No need to handle the reject promise because onError will take care of it.
        }

        // If there's no subscription that means an error has been thrown
        if (!subscription) {
          return
        }

        /**
         * If "pendingScaToken" is present on the payload we need to do the
         * challenge with stripe to make sure the card is not INCOMPLETE
         */
        if (subscription.pendingScaToken) {
          await confirmPendingSetupIntent(stripe, subscription.pendingScaToken)
        }

        // We might need to fresh the UI due to the payment update
        await onPaymentUpdated?.()

        if (subscription) {
          showToast('Your payment method has been updated')
          hideModal()
        }
      }

      actions.setSubmitting(false)
    }
  }

  return (
    <Modal onCancel={hideModal}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        validate={stripeValidation}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, errors }) => (
          <Form>
            <Fieldset disabled={isSubmitting || loading}>
              <Modal.Header>Payment Method</Modal.Header>
              <Modal.Body>
                <CreditCards />
                <CreditCardForm<FormValues> />
                {!isWorkspaceSubscriptionActive && (
                  <Banner type="warning" showIcon={false}>
                    Your subscription will be reactivated if you update your
                    card details.
                  </Banner>
                )}
                <Caption>
                  <br />
                  This card <Strong>will replace</Strong> your current card and
                  be used on your next bill.
                </Caption>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={hideModal}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    isSubmitting || loading || Object.keys(errors).length > 0
                  }
                  loading={isSubmitting || loading}
                >
                  Update Billing Details
                </Button>
              </Modal.Footer>
            </Fieldset>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default EditPaymentMethodModal
